"use client";

import * as React from "react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { RANGES } from "@/components/time-range-selector";
import { useIsMobile } from "@/hooks/use-mobile";
import type { UserDailyCount } from "@/providers/admin.provider";

const chartConfig = {
  count: {
    label: "New users",
    color: "var(--primary)",
  },
} satisfies ChartConfig;

// Fill sparse API data so the chart has a point for every day in the window.
// Uses local calendar dates (not UTC) so keys match the YYYY-MM-DD strings the API returns.
function fillDailyData(sparse: UserDailyCount[], days: number) {
  const byDate = new Map(sparse.map((d) => [d.date.slice(0, 10), d.count]));
  const result: { date: string; count: number }[] = [];

  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const dd = now.getDate();

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(y, m, dd - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    result.push({ date: key, count: byDate.get(key) ?? 0 });
  }

  return result;
}

interface Props {
  data: UserDailyCount[];
  days: number;
  onDaysChange: (days: number) => void;
  isLoading: boolean;
  onlineUsers?: number;
  allHistoryData?: UserDailyCount[];
}

export function ChartAreaInteractive({
  data,
  days,
  onDaysChange,
  isLoading,
  onlineUsers = 0,
  allHistoryData = [],
}: Props) {
  const isMobile = useIsMobile();
  const daysStr = String(days);
  const [cumulative, setCumulative] = React.useState(true);

  // On mobile default to 30 days if the parent hasn't overridden it yet
  React.useEffect(() => {
    if (isMobile && days === 365) onDaysChange(30);
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const dailyData = React.useMemo(
    () => fillDailyData(data, days),
    [data, days],
  );

  const totalUsersEverRegistered = React.useMemo(() => {
    const historyTotal = allHistoryData.reduce((sum, d) => sum + d.count, 0);
    return Math.max(historyTotal, onlineUsers);
  }, [allHistoryData, onlineUsers]);

  const chartData = React.useMemo(() => {
    if (!cumulative) return dailyData;

    // The starting point for our range is: total ever registered - sum of daily new users in the visible range
    const newUsersInRange = dailyData.reduce((sum, d) => sum + d.count, 0);
    let startingUsers = Math.max(0, totalUsersEverRegistered - newUsersInRange);

    return dailyData.map((d) => ({
      date: d.date,
      count: (startingUsers += d.count),
    }));
  }, [dailyData, cumulative, totalUsersEverRegistered]);

  const totalNewUsers = React.useMemo(
    () => data.reduce((sum, d) => sum + d.count, 0),
    [data],
  );

  const rangeLabel =
    RANGES.find((r) => r.value === daysStr)?.label ?? `${days} days`;

  return (
    <Card className="@container/card">
      <CardHeader>
        <div className="flex flex-col gap-1">
          <CardTitle>
            {cumulative ? "Total users" : "New registrations"}
          </CardTitle>
          <CardDescription>
            <span className="hidden @[540px]/card:block">
              {isLoading
                ? "Loading…"
                : cumulative
                  ? `${totalUsersEverRegistered.toLocaleString()} total users`
                  : `${totalNewUsers.toLocaleString()} new users — last ${rangeLabel}`}
            </span>
            <span className="@[540px]/card:hidden">
              {isLoading ? "Loading…" : `Last ${rangeLabel}`}
            </span>
          </CardDescription>
          <div className="flex items-center gap-2 mt-1">
            <Switch
              id="cumulative-toggle"
              checked={cumulative}
              onCheckedChange={setCumulative}
            />
            <Label
              htmlFor="cumulative-toggle"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Total Users
            </Label>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        {isLoading ? (
          <Skeleton className="aspect-auto h-[250px] w-full rounded-lg" />
        ) : (
          <ChartContainer
            config={chartConfig}
            className="aspect-auto h-[250px] w-full"
          >
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="fillCount" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="var(--color-count)"
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor="var(--color-count)"
                    stopOpacity={0.05}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={days <= 30 ? 6 : 32}
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                }
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                allowDecimals={false}
                width={32}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    labelFormatter={(value) =>
                      new Date(value).toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })
                    }
                    indicator="dot"
                  />
                }
              />
              <Area
                dataKey="count"
                type="monotone"
                fill="url(#fillCount)"
                stroke="var(--color-count)"
              />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
